import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import ErrorBoundary from '@/components/ErrorBoundary';

// Import pages
import LoginPage from '@/pages/LoginPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import MyAccountPage from '@/pages/MyAccountPage';
import DashboardPage from '@/pages/DashboardPage';
import UserManagementPage from '@/pages/UserManagementPage';
import StandortePage from '@/pages/StandortePage';
import StandortDetailPage from '@/pages/StandortDetailPage';
import StandortScanPage from '@/pages/StandortScanPage';
import AutomatenPage from '@/pages/AutomatenPage';
import AutomatDetailPage from '@/pages/AutomatDetailPage';
import AutomatAuszaehlenPage from '@/pages/AutomatAuszaehlenPage';
import AutomatAusleerenPage from '@/pages/AutomatAusleerenPage';
import BefuellungPage from '@/pages/BefuellungPage';
import ProdukteVerwaltungPage from '@/pages/ProdukteVerwaltungPage';
import LagerPage from '@/pages/LagerPage';
import InventoryPage from '@/pages/InventoryPage';
import HelperInventoryPage from '@/pages/HelperInventoryPage';
import TourenplanungPage from '@/pages/TourenplanungPage';
import FeatureDisabledPage from '@/pages/FeatureDisabledPage';
import ReportsPage from '@/pages/ReportsPage';
import KassenbuchPage from '@/pages/KassenbuchPage';
import TimeTrackingPage from '@/pages/TimeTrackingPage';
import BackupManagementPage from '@/pages/BackupManagementPage';
import ReleaseNotesPage from '@/pages/ReleaseNotesPage';
import ForbiddenPage from '@/pages/ForbiddenPage';
import NotFoundPage from '@/pages/NotFoundPage';
import Layout from '@/components/Layout';

// ============================================================
// FEATURE FLAGS - Temporär ausgeblendete Features
// ============================================================
const FEATURE_FLAGS = {
  TOURENPLANUNG_ENABLED: false,
};

// ============================================================
// ADMIN ROUTE GUARD - Schützt Admin-only Routes
// ============================================================
const AdminRoute = ({ children }) => {
  const { user } = React.useContext(AuthContext);
  
  if (user?.role !== 'admin') {
    return <ForbiddenPage />;
  }
  
  return children;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext(null);

// Axios interceptor for auth
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Setze Redirect-Reason für Login-Page
      const reason = error.response?.data?.detail || 'Session abgelaufen oder Token ungültig';
      sessionStorage.setItem('auth_redirect_reason', reason);
      
      // Dev-Mode Logging
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Auth Guard] 401 Error - Redirecting to login:', reason);
      }
      
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    // Dev-Mode Debug Banner
    if (process.env.NODE_ENV === 'development') {
      console.log('[Auth] Logged in as:', userData.email, 'Role:', userData.role);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Laden...</div>;
  }

  return (
    <ErrorBoundary>
      <AuthContext.Provider value={{ user, login, logout, API }}>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={!user ? <LoginPage /> : <Navigate to="/" />} />
            <Route path="/forgot-password" element={!user ? <ForgotPasswordPage /> : <Navigate to="/" />} />
            
            <Route path="/" element={user ? <Layout /> : <Navigate to="/login" />}>
              <Route index element={<DashboardPage />} />
              <Route path="mein-konto" element={<MyAccountPage />} />
              <Route path="standorte" element={<StandortePage />} />
              <Route path="standorte/:id" element={<StandortDetailPage />} />
              <Route path="standorte/scan/:qr" element={<StandortScanPage />} />
              <Route path="automaten" element={<AutomatenPage />} />
              <Route path="automaten/:id" element={<AutomatDetailPage />} />
              <Route path="automaten/auszaehlen/:qr" element={<AutomatAuszaehlenPage />} />
              <Route path="automaten/auszaehlen-id/:id" element={<AutomatAuszaehlenPage />} />
              <Route path="befuellung/:automatId" element={<BefuellungPage />} />
              <Route path="produkte" element={<ProdukteVerwaltungPage />} />
              <Route path="lager" element={<LagerPage />} />
              <Route path="inventar" element={<InventoryPage />} />
              <Route path="werkstatt-inventar" element={<HelperInventoryPage />} />
              {/* Tourenplanung: Temporär deaktiviert via Feature-Flag */}
              <Route 
                path="tourenplanung" 
                element={
                  FEATURE_FLAGS.TOURENPLANUNG_ENABLED 
                    ? <TourenplanungPage /> 
                    : <FeatureDisabledPage featureName="Tourenplanung" />
                } 
              />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="kassenbuch" element={<KassenbuchPage />} />
              <Route path="zeiterfassung" element={<TimeTrackingPage />} />
              <Route path="users" element={<AdminRoute><UserManagementPage /></AdminRoute>} />
              <Route path="backups" element={<AdminRoute><BackupManagementPage /></AdminRoute>} />
              <Route path="release-notes" element={<AdminRoute><ReleaseNotesPage /></AdminRoute>} />
              {/* Catch-all for authenticated users */}
              <Route path="*" element={<NotFoundPage />} />
            </Route>
            
            {/* Catch-all for non-authenticated users */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthContext.Provider>
    </ErrorBoundary>
  );
}

export default App;